import { link } from "@/components/Action.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { type WTabItem, WTabs } from "@/components/WTabs.tsx";
import { InfoTooltip, WTooltip } from "@/components/WTooltip.tsx";
import { DeleteButton } from "@/components/button/DeleteButton.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { WModalButton, WToolButton } from "@/components/button/WToolButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccount } from "@/components/table/DetailsTableBillingAccount.tsx";
import { DetailsTableIpAddress } from "@/components/table/DetailsTableIpAddress.tsx";
import { MonthlyCostRow } from "@/components/table/DetailsTableMonthlyCost.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { useConfig } from "@/config.ts";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { VpcRow } from "@/modules/network/VpcRow.tsx";
import {
    allIpAddressesLabel,
    firewallDescription,
    firewallInboundDescription,
    firewallOutboundDescription,
} from "@/modules/network/firewall/firewallLabels.tsx";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useQueryResultAtom, useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import type {
    BillingAccount,
    FirewallRuleDirectionType,
    VirtualMachineStorageReplica,
    VmInterface,
} from "@warrenio/api-spec/spec.oats.gen";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { useState } from "react";
import { first, sortBy } from "remeda";

import { PriceWithDiscount } from "@/components/PriceWithDiscount.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { WSwitch } from "@/components/forms/WSwitch.tsx";
import { AddFirewallForm } from "@/modules/compute/AddFirewallForm.tsx";
import { AddSubInterfaceButton } from "@/modules/compute/AddSubInterfaceModal.tsx";
import { BootMode } from "@/modules/compute/BootMode.tsx";
import { DiskAddOrResizeModal } from "@/modules/compute/DiskAddOrResizeModal.tsx";
import { DiskDeleteModal } from "@/modules/compute/DiskDeleteModal.tsx";
import { MetricsGrid } from "@/modules/compute/MetricsGrid.tsx";
import { SubInterfaceDeleteModal } from "@/modules/compute/SubInterfaceDeleteModal.tsx";
import { SubInterfaceModal } from "@/modules/compute/SubInterfaceModal.tsx";
import { VmDeleteModal } from "@/modules/compute/VmDeleteModal.tsx";
import { VmDeleteReplicaModal } from "@/modules/compute/VmDeleteReplicaModal.tsx";
import { VmEstimatedReplicaPriceModal } from "@/modules/compute/VmEstimatedReplicaPriceModal.tsx";
import { VmResizeInfoButton } from "@/modules/compute/VmResizeModal.tsx";
import { VmRestoreFromReplicaModal } from "@/modules/compute/VmRestoreFromReplicaModal.tsx";
import { VmStatusBadge } from "@/modules/compute/VmStatusBadge.tsx";
import { VmToolbar } from "@/modules/compute/VmToolbar.tsx";
import {
    type AssignedVmResponse,
    assignedVmsQueryAtom,
    type VirtualMachineWithAssigned,
} from "@/modules/compute/joinAssignedQuery.ts";
import { description_snapshots } from "@/modules/compute/labels.tsx";
import { OsInfo } from "@/modules/compute/os/OsInfo.tsx";
import { extractOsFields } from "@/modules/compute/os/os.ts";
import { getPrimaryStorage, getReplicasByType } from "@/modules/compute/vmEntityUtils.ts";
import { vmInterfacesQueryAtom } from "@/modules/compute/vmInterfacesQuery.ts";
import {
    changeVmBillingAccountMutation,
    createSnapshotMutation,
    deleteVmMutation,
    enableDisableBackupMutation,
    modifyVmMutation,
    type VirtualMachineLoc,
} from "@/modules/compute/vmQuery.ts";
import {
    firewallQueryAtom,
    type FirewallWithType,
    removeResourceFromFirewallMutation,
} from "@/modules/network/firewall/firewallApi.ts";
import { getRuleType } from "@/modules/network/firewall/ruleSpec.tsx";
import type { AssignedIpAddress } from "@/modules/network/ipAddress/assignable.ts";
import * as ipQuery from "@/modules/network/ipAddress/ipAddressApi.ts";
import { firewallLink } from "@/modules/network/links.ts";
import type { VpcWithType } from "@/modules/network/vpc/vpcApi.ts";
import { vpcQueryAtom } from "@/modules/network/vpc/vpcApi.ts";
import { hostPoolQueryAtom } from "@/modules/pools/hostPoolsQuery.ts";

export function VmView({ vmId, location }: { vmId: string; location: string }) {
    //#region Hooks

    const { privateNetworksEnabled, firewallEnabled } = useConfig();

    const vms = useSuspenseQueryAtom(assignedVmsQueryAtom(location));
    // Progressive load VPC tab
    const { data: vpcs } = useQueryResultAtom(vpcQueryAtom(location));

    const deleteVmMutation = useDeleteVmMutation();
    const vm = useDeletableResource(() => getResourceById(vms, vmId, "virtual_machine"), deleteVmMutation);
    //#endregion

    if (vm === undefined) {
        return <NavigateAfterDelete />;
    }

    const { uuid } = vm;

    const vpc = vpcs ? [...vpcs.values()].find((item: VpcWithType) => item.vm_uuids.includes(uuid)) : undefined;
    const hasNetworkTab = privateNetworksEnabled && !!vpc;

    const tabs = filterFalse<WTabItem>([
        { id: "metrics", title: <Trans>Metrics</Trans>, content: <MetricsContent vm={vm} /> },
        { id: "disks", title: <Trans>Disks</Trans>, content: <DisksContent vm={vm} /> },
        hasNetworkTab &&
            !!vpc && {
                id: "networking",
                title: <Trans>Networking</Trans>,
                content: <NetworkingContent vm={vm} />,
            },
        firewallEnabled && {
            id: "firewall",
            title: <Trans>Firewall</Trans>,
            content: <FirewallContent vm={vm} />,
        },
        {
            id: "backups",
            title: <Trans>Backups</Trans>,
            content: <BackupsContent vm={vm} vms={vms} />,
        },
        {
            id: "snapshots",
            title: <Trans>Snapshots</Trans>,
            content: <SnapshotsContent vm={vm} vms={vms} vpc={vpc} />,
        },
    ]);

    return (
        <>
            <VmToolbar vm={vm} deleteModal={<VmDeleteModal {...{ vm, deleteVmMutation }} />} />
            <VmDetails vm={vm} vpc={vpc} />
            <Separator />
            <WTabs autoSuspense allTab items={tabs} />
        </>
    );
}

function useDeleteVmMutation() {
    return useStandardMutation(deleteVmMutation);
}

export type DeleteVmMutation = ReturnType<typeof useDeleteVmMutation>;

function VmDetails({ vm, vpc }: { vm: VirtualMachineWithAssigned; vpc: VpcWithType | undefined }) {
    //#region Hooks
    const { t } = useLingui();
    const { data: pools } = useQueryResultAtom(hostPoolQueryAtom(vm.location));

    const modifyMutation = useStandardMutation(modifyVmMutation);
    const changeBillingAccountMutation = useStandardMutation(changeVmBillingAccountMutation);
    //#endregion

    const {
        $type,
        billing_account,
        created_at,
        designated_pool_uuid,
        location,
        mac,
        name,
        private_ipv4,
        public_ipv6,
        status,
        username,
        uuid,
        price,
        boot_firmware,
        billing_account: baId,
    } = vm;

    const pool = designated_pool_uuid ? pools?.get(designated_pool_uuid) : undefined;

    async function onRename(newName: string) {
        newName = newName.trim();
        if (newName && vm.name !== newName) {
            newName = newName.trim();
            await modifyMutation.mutateAsync({
                body: { name: newName, uuid },
                location,
            });
        }
    }

    async function changeBillingAccount(billing_account: BillingAccount) {
        await changeBillingAccountMutation.mutateAsync({ location, uuid, billing_account_id: billing_account.id });
    }

    return (
        <ContentPane className="VStack gap-4">
            <ViewTitle title={name} />

            <DetailsHolder>
                <DetailsTable>
                    <DetailsTableName value={name} isRequired onChange={async (name) => await onRename(name)} />

                    <DetailsTableRow title={t`Type`}>
                        <ResourceWithIcon type={$type} />
                    </DetailsTableRow>

                    <DetailsTableRow title={<Trans comment="Short of: Operating system">OS</Trans>}>
                        <OsInfo os={extractOsFields(vm)} />
                    </DetailsTableRow>

                    <DetailsTableRow title={t`Status`}>
                        <VmStatusBadge value={status} />
                    </DetailsTableRow>

                    {public_ipv6 != null && (
                        <DetailsTableRow title={t`Public IPv6`}>
                            <ClipBoardTooltip isHtml>{public_ipv6}</ClipBoardTooltip>
                        </DetailsTableRow>
                    )}

                    <VmPublicIp item={vm} />

                    <DetailsTableRow title={t`Private IPv4`}>
                        <ClipBoardTooltip isHtml>{private_ipv4}</ClipBoardTooltip>
                    </DetailsTableRow>

                    <VpcRow vpc={vpc} />

                    <DetailsTableRow title={t`Size`}>
                        <VmResizeInfoButton vm={vm} />
                    </DetailsTableRow>
                </DetailsTable>

                <Spacer />

                <DetailsTable>
                    <DetailsTableRow title={t`UUID`}>
                        <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                    </DetailsTableRow>

                    {!!username && <DetailsTableRow title={t`Username`}>{username}</DetailsTableRow>}

                    <DetailsTableRow title={t`MAC`}>{mac}</DetailsTableRow>

                    <LocationDetail slug={location} />

                    {pool ? (
                        <DetailsTableRow title={t`Server class`}>
                            <div className="flex">
                                {pool.description ? (
                                    <InfoTooltip text={pool.description}>{pool.name}</InfoTooltip>
                                ) : (
                                    pool.name
                                )}
                            </div>
                        </DetailsTableRow>
                    ) : (
                        <DetailsTableRow />
                    )}

                    <DetailsTableRow title={t`Boot mode`}>
                        <BootMode value={boot_firmware} />
                    </DetailsTableRow>

                    <DetailsTableRow title={t`Created`}>
                        <LongDate date={created_at} />
                    </DetailsTableRow>
                    <DetailsTableRow />
                    <DetailsTableBillingAccount
                        valueKey={billing_account}
                        onChange={async (item) => await changeBillingAccount(item.account)}
                    />

                    <MonthlyCostRow price={price} billingAccountId={baId} />
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

function VmPublicIp({ item }: { item: VirtualMachineWithAssigned }) {
    const { location, uuid, assignedPublicIp } = item;
    const unAssignIpMutation = useStandardMutation(ipQuery.unassignIpAddressMutation);
    const assignIpAddressMutation = useStandardMutation(ipQuery.assignIpAddressMutation);

    async function assignIpAddress(address: AssignedIpAddress | null) {
        if (address) {
            await assignIpAddressMutation.mutateAsync({
                location,
                address: address.address,
                body: {
                    assigned_to_resource_type: "virtual_machine",
                    assigned_to: uuid,
                },
            });
        } else if (assignedPublicIp) {
            await unAssignIpMutation.mutateAsync({ location, address: assignedPublicIp.address });
        }
    }

    return (
        <DetailsTableIpAddress
            value={assignedPublicIp}
            location={location}
            onChange={async (obj) => await assignIpAddress(obj)}
        />
    );
}

function MetricsContent({ vm }: { vm: VirtualMachineLoc }) {
    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Metrics</Trans>} />
            <MetricsGrid vm={vm} />
        </ContentPane>
    );
}

function sortByCreatedAtDesc<T extends { created_at: string }>(items: T[]) {
    return sortBy(items, [(item) => apiDatetimeToDate(item.created_at).getTime(), "desc"]);
}

function DisksContent({ vm }: { vm: VirtualMachineWithAssigned }) {
    const { t } = useLingui();
    // NB: Not sorted by created_at, because the boot disk should be shown as the first one
    const { storage } = vm;

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Disks</Trans>} />

            <WTable
                afterTable={
                    <WModal
                        button={
                            // TODO: Create a common component for "table footer" type buttons
                            <WButton
                                color="primary"
                                size="bar"
                                variant="border"
                                icon="jp-stack-icon"
                                action={undefined}
                            >
                                <Trans>Add additional disk</Trans>
                            </WButton>
                        }
                    >
                        <DiskAddOrResizeModal vm={vm} disk={null} />
                    </WModal>
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>UUID</Trans>
                        </th>
                        <th>
                            <Trans>Size (GB)</Trans>
                        </th>
                        <th>
                            <Trans>Created</Trans>
                        </th>
                        <th>
                            <Trans>Est. monthly cost</Trans>
                        </th>
                        <th>
                            <Trans comment="If the disk is a boot disk">Mode</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {storage.map((item) => (
                        <tr key={item.uuid}>
                            <td>
                                <ClipBoardTooltip>{item.uuid}</ClipBoardTooltip>
                            </td>
                            <td>{item.size}</td>
                            <td>
                                <LongDate date={item.created_at} />
                            </td>
                            <td>
                                <PriceWithDiscount
                                    price={vm.priceCalc.getDiskPrice(item)}
                                    billingAccountId={vm.billing_account}
                                    allowFree
                                />
                            </td>
                            <td>{item.primary ? "Boot" : "-"}</td>
                            <td className="text-right">
                                <div className="flex gap-0.5em justify-end">
                                    <WModal button={<WModalButton label={t`Resize`} inTable icon="jp-expand-2-icon" />}>
                                        <DiskAddOrResizeModal vm={vm} disk={item} />
                                    </WModal>
                                    <DiskDeleteModal vm={vm} disk={item} />
                                </div>
                            </td>
                        </tr>
                    ))}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}

class PrimaryNetworkInterfaceNotFound extends Error {
    constructor(uuid: string) {
        super(`Primary network interface not found for VM ${uuid}, has the VM been deleted?`);
    }
}

function NetworkingContent({ vm }: { vm: VirtualMachineWithAssigned }) {
    const { t } = useLingui();
    const { uuid, assignedPublicIp, location } = vm;
    const interfaces: VmInterface[] = useSuspenseQueryAtom(vmInterfacesQueryAtom(location, uuid));

    const vmi = first(interfaces);
    if (!vmi) {
        throw new PrimaryNetworkInterfaceNotFound(uuid);
    }

    const publicIPv6Address = vmi.public_ipv6_address?.address;
    const privateIPv4Address = !vmi.private_ip_address.is_ipv6 ? vmi.private_ip_address.address : undefined;
    const description = (
        /* NB: The lead-in and its list form a single unit, hence the tighter gap between them. */
        <div className="VStack gap-1">
            <p>
                <Trans>This virtual machine is accessible by:</Trans>
            </p>
            <ul className="m-0 p-0 pl-4">
                <li>
                    <Trans comment="VM is accessible by:">
                        resources in the same VPC network using private addresses of primary interfaces
                    </Trans>
                </li>
                <li>
                    <Trans comment="VM is accessible by:">
                        resources in the VPCs shared by VLAN sub-interfaces using their respective private address
                    </Trans>
                </li>
            </ul>
        </div>
    );

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Network interfaces</Trans>} description={description} />

            <WTable
                afterTable={
                    <AddSubInterfaceButton
                        uuid={uuid}
                        vmiUuid={vmi.vmi_uuid}
                        networkUuid={vmi.network.uuid}
                        location={location}
                        button={
                            <WButton color="primary" size="bar" variant="border" icon="jp-icon-add" action={undefined}>
                                <Trans>Add sub-interface</Trans>
                            </WButton>
                        }
                    />
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>Type</Trans>
                        </th>
                        <th>
                            <Trans>VLAN ID</Trans>
                        </th>
                        <th>
                            <Trans>Private IPv4</Trans>
                        </th>
                        <th>
                            <Trans>Public IPv4</Trans>
                        </th>
                        <th>
                            <Trans>Public IPv6</Trans>
                        </th>
                        <th>
                            <Trans comment="MAC address">MAC</Trans>
                        </th>
                        <th>
                            <Trans>VPC name</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    <tr>
                        <td className="whitespace-nowrap">
                            <MaskIcon className="color-primary jp-icon-caretdown size-0.875rem mr-1" />
                            <Trans>Primary</Trans>
                        </td>
                        <td>-</td>
                        <td>
                            {privateIPv4Address ? (
                                <ClipBoardTooltip text={privateIPv4Address}>
                                    <span className="color-primary">{privateIPv4Address}</span>/
                                    {vmi.network.subnet.split("/")[1]}
                                </ClipBoardTooltip>
                            ) : (
                                "-"
                            )}
                        </td>
                        <td>
                            {assignedPublicIp?.address ? (
                                <ClipBoardTooltip>{assignedPublicIp?.address}</ClipBoardTooltip>
                            ) : (
                                "-"
                            )}
                        </td>
                        <td>{publicIPv6Address ? <ClipBoardTooltip>{publicIPv6Address}</ClipBoardTooltip> : "-"}</td>
                        <td>{vmi.vmi_mac}</td>
                        <td>
                            <ResourceWithIcon type="vpc" title={vmi.network.name} />
                        </td>
                        <td>
                            <AddSubInterfaceButton
                                uuid={uuid}
                                vmiUuid={vmi.vmi_uuid}
                                networkUuid={vmi.network.uuid}
                                location={location}
                                button={
                                    <WTooltip text={t`Add sub-interface`}>
                                        <WButton
                                            color="primary"
                                            size="xs"
                                            variant="ghost"
                                            icon="jp-icon-add"
                                            action={undefined}
                                        />
                                    </WTooltip>
                                }
                            />
                        </td>
                    </tr>

                    {vmi.subinterfaces.map((item) => {
                        const _privateIPv4Address =
                            item.ip_address.type === "private" && !item.ip_address.is_ipv6
                                ? item.ip_address.address
                                : undefined;

                        return (
                            <tr key={item.id}>
                                <td className="whitespace-nowrap" style={{ paddingLeft: "26px" }}>
                                    <WTooltip text={t`View config commands`}>
                                        <SubInterfaceModal
                                            vlanTag={item.vlan_tag}
                                            macAddr={item.mac_addr}
                                            ipAddr={_privateIPv4Address ?? ""}
                                        />
                                    </WTooltip>
                                </td>
                                <td>{item.vlan_tag}</td>
                                <td>
                                    {_privateIPv4Address ? (
                                        <ClipBoardTooltip text={_privateIPv4Address}>
                                            <span className="color-primary">{_privateIPv4Address}</span>/
                                            {item.network.subnet.split("/")[1]}
                                        </ClipBoardTooltip>
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td>-</td>
                                <td>-</td>
                                <td>{item.mac_addr}</td>
                                <td>
                                    <ResourceWithIcon type="vpc" title={item.network.name} />
                                </td>
                                <td>
                                    <SubInterfaceDeleteModal uuid={vm.uuid} ifUuid={item.uuid} location={location} />
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}

function FirewallRuleContent({
    items,
    direction,
}: {
    items: FirewallWithType[];
    direction: FirewallRuleDirectionType;
}) {
    const allRules = items.flatMap((item) =>
        item.rules.map((rule) => ({
            ...rule,
            firewall: item,
        })),
    );

    return (
        <>
            <SectionHeader
                title={direction === "inbound" ? <Trans>Inbound rules</Trans> : <Trans>Outbound rules</Trans>}
                description={direction === "inbound" ? firewallInboundDescription : firewallOutboundDescription}
            />
            <WTable>
                <thead>
                    <tr>
                        <th>
                            <Trans>Type</Trans>
                        </th>
                        <th>
                            <Trans>Protocol</Trans>
                        </th>
                        <th>
                            <Trans>Port range</Trans>
                        </th>
                        <th>{direction === "inbound" ? <Trans>Sources</Trans> : <Trans>Destinations</Trans>}</th>
                        <th>
                            <Trans>Firewall name</Trans>
                        </th>
                    </tr>
                </thead>
                <WTableBody>
                    {allRules
                        .filter((item) => item.direction === direction)
                        .map((item) => {
                            return (
                                <tr key={item.uuid}>
                                    <td>{getRuleType(item)}</td>
                                    <td>{item.protocol.toUpperCase()}</td>
                                    <td>
                                        {item.port_start ? (
                                            item.port_start < 0 ? (
                                                "-"
                                            ) : (
                                                item.port_start
                                            )
                                        ) : (
                                            <Trans>N/A</Trans>
                                        )}
                                        {!!item.port_end && <> - {item.port_end}</>}
                                    </td>
                                    <td>
                                        {item.endpoint_spec?.length
                                            ? item.endpoint_spec.join(", ")
                                            : allIpAddressesLabel}
                                    </td>
                                    <td>
                                        <WButton
                                            color="primary"
                                            size="xs"
                                            variant="ghost"
                                            action={firewallLink(item.firewall)}
                                        >
                                            {item.firewall.display_name}
                                        </WButton>
                                    </td>
                                </tr>
                            );
                        })}
                </WTableBody>
            </WTable>
        </>
    );
}

function FirewallContent({ vm }: { vm: VirtualMachineWithAssigned }) {
    const { uuid, location } = vm;
    const firewalls = useSuspenseQueryAtom(firewallQueryAtom(location));
    const removeMutation = useStandardMutation(removeResourceFromFirewallMutation);
    const [isVisible, setIsVisible] = useState(false);

    const firewallsByAssignedTo = new Map<string, FirewallWithType[]>();

    for (const firewall of firewalls.values()) {
        if (!firewall.resources_assigned) continue;

        for (const res of firewall.resources_assigned) {
            if (res.resource_type === "vm") {
                const list = firewallsByAssignedTo.get(res.resource_uuid) ?? [];
                list.push(firewall);
                firewallsByAssignedTo.set(res.resource_uuid, list);
            }
        }
    }

    const assignedFirewalls = firewallsByAssignedTo.get(uuid) ?? [];

    async function removeResource(firewall_uuid: string) {
        await removeMutation.mutateAsync({ location, uuid: firewall_uuid, vm_uuid: uuid });
    }

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Firewall</Trans>} description={firewallDescription} />

            <WTable
                afterTable={
                    !isVisible && (
                        <WButton
                            color="primary"
                            variant="border"
                            size="bar"
                            icon="jp-icon-add"
                            action={() => setIsVisible(!isVisible)}
                        >
                            <Trans>Attach firewall</Trans>
                        </WButton>
                    )
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>Name</Trans>
                        </th>
                        <th>
                            <Trans>No. of resources</Trans>
                        </th>
                        <th>
                            <Trans>No. of rules</Trans>
                        </th>
                        <th>
                            <Trans>Created</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {assignedFirewalls.map((item) => {
                        return (
                            <tr key={item.uuid}>
                                <td>
                                    <WButton color="primary" size="xs" variant="ghost" action={firewallLink(item)}>
                                        {item.display_name}
                                    </WButton>
                                </td>
                                <td>{item.resources_assigned?.length}</td>
                                <td>{item.rules.length}</td>
                                <td>
                                    <LongDate date={item.created_at} />
                                </td>
                                <td className="text-right">
                                    <DeleteButton inTable action={async () => await removeResource(item.uuid)} />
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
                {isVisible && (
                    <tfoot>
                        <tr>
                            <td colSpan={5}>
                                <AddFirewallForm
                                    vmUuid={uuid}
                                    location={location}
                                    onClose={() => setIsVisible(false)}
                                />
                            </td>
                        </tr>
                    </tfoot>
                )}
            </WTable>

            <FirewallRuleContent items={assignedFirewalls} direction="inbound" />

            <FirewallRuleContent items={assignedFirewalls} direction="outbound" />
        </ContentPane>
    );
}

function BackupsContent({
    vm,
    vms,
    vpc,
}: {
    vm: VirtualMachineWithAssigned;
    vms: AssignedVmResponse;
    vpc?: VpcWithType;
}) {
    //#region Hooks
    const { t } = useLingui();
    const { backupTime } = useConfig();
    const navigate = useNavigate();
    const toggleBackupsMutation = useStandardMutation(enableDisableBackupMutation);
    //#endregion

    const items = sortByCreatedAtDesc(getReplicasByType(vm, "backup"));
    const latestItem = first(items);

    const { backup: backupsEnabled, priceCalc, billing_account } = vm;
    const primary = getPrimaryStorage(vm);
    const size = primary?.size ?? 0;
    const price = priceCalc.getReplicaPrice({ size, type: "backup" });
    const monthlyCost = { ...price, hourly: price.hourly * 4 };

    async function toggleBackups(vm: VirtualMachineLoc) {
        const { location, uuid } = vm;
        await toggleBackupsMutation.mutateAsync({ location, body: { uuid } });
    }

    const switchControl = (
        <WSwitch isSelected={backupsEnabled} onChange={backupsEnabled ? undefined : () => toggleBackups(vm)} />
    );

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Backups</Trans>} />

            {backupsEnabled ? (
                <p className="color-muted">
                    <Trans>
                        Automatic system-level backups are enabled. Use the backup images to restore your virtual
                        machine or create a new virtual machine from the backup with modified size parameters.
                    </Trans>
                </p>
            ) : (
                <p className="color-muted">
                    <Trans>
                        When you enable the backup service, an automatic disk-level backup will be created from the boot
                        disk (excluding additional disks). A backup will be created once a week and will be listed here.
                        You can later use the backup images to restore your virtual machine or create a new virtual
                        machine with modified size parameters from the backup.
                    </Trans>
                </p>
            )}

            <DetailsTable>
                <DetailsTableRow title={t`Retention`}>
                    <Trans>4 weeks</Trans>
                </DetailsTableRow>

                <DetailsTableRow title={t`Frequency`}>
                    {backupTime ? backupTime : t`Once a week (Wednesday at 02:00 UTC)`}
                </DetailsTableRow>

                <DetailsTableRow title={t`Est. cost per backup`}>
                    <PriceWithDiscount price={price} billingAccountId={billing_account} allowFree />
                </DetailsTableRow>

                <DetailsTableRow title={t`Est. monthly cost`}>
                    <PriceWithDiscount price={monthlyCost} billingAccountId={billing_account} allowFree />
                </DetailsTableRow>

                {!!latestItem && (
                    <DetailsTableRow title={t`Latest backup`}>
                        <LongDate date={latestItem.created_at} />
                    </DetailsTableRow>
                )}
            </DetailsTable>

            {(backupsEnabled || items.length > 0) && (
                <WTable>
                    <thead>
                        <tr>
                            <th>
                                <Trans>UUID</Trans>
                            </th>
                            <th>
                                <Trans>Size (GB)</Trans>
                            </th>
                            <th>
                                <Trans>Created</Trans>
                            </th>
                            <th />
                        </tr>
                    </thead>
                    <WTableBody>
                        {items.map((item) => (
                            <tr key={item.uuid}>
                                <td>
                                    <ClipBoardTooltip>{item.uuid}</ClipBoardTooltip>
                                </td>
                                <td>{item.size}</td>
                                <td>
                                    <LongDate date={item.created_at} />
                                </td>
                                <td className="text-right">
                                    <div className="flex gap-0.5em justify-end">
                                        <WToolButton
                                            icon="jp-icon-add"
                                            label={t`Create new`}
                                            inTable
                                            action={async () =>
                                                await navigate(
                                                    createVmFromReplicaLink(
                                                        getSourceVmForReplica(vms, vm, item),
                                                        item,
                                                        vpc,
                                                    ),
                                                )
                                            }
                                        />

                                        <VmRestoreFromReplicaModal vm={vm} replica={item} inTable />
                                        <VmDeleteReplicaModal vm={vm} replica={item} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </WTableBody>
                </WTable>
            )}

            {backupsEnabled ? (
                <WModal button={switchControl}>
                    <WModalContent title={t`Disable backups`} label={t`Disable`} modalAction={() => toggleBackups(vm)}>
                        <Trans>
                            By disabling backups there will not be any further automatic backups made from your virtual
                            machine. Backups done by this point will be kept as is and you can still use them. You can
                            enable backups again whenever needed.
                        </Trans>
                    </WModalContent>
                </WModal>
            ) : (
                switchControl
            )}
        </ContentPane>
    );
}

function createVmFromReplicaLink(
    vm: VirtualMachineWithAssigned,
    storage: VirtualMachineStorageReplica,
    vpc: VpcWithType | undefined,
) {
    return link({
        to: "/compute/create",
        state: {
            vmCreateInputs: {
                source_uuid: vm.uuid,
                source_replica: storage.uuid,

                os: {
                    os_name: vm.os_name,
                    os_version: vm.os_version,
                },
                location: vm.location,
                size: {
                    vcpu: vm.vcpu,
                    ram: vm.memory,
                    disks: storage.size,
                    isCustom: false,
                },
                typeId: "plain_os",
                reserve_public_ip: !!vm.assignedPublicIp,
                network_uuid: vpc?.uuid,
                designated_pool_uuid: vm.designated_pool_uuid,
                name: `${vm.name}-from-${storage.type}`,
            },
        },
    });
}

function getSourceVmForReplica(
    vms: AssignedVmResponse,
    fallbackVm: VirtualMachineWithAssigned,
    replica: VirtualMachineStorageReplica,
) {
    return [...vms.values()].find((vm) => vm.storage.some((disk) => disk.uuid === replica.master_uuid)) ?? fallbackVm;
}

function SnapshotsContent({
    vm,
    vms,
    vpc,
}: {
    vm: VirtualMachineWithAssigned;
    vms: AssignedVmResponse;
    vpc?: VpcWithType;
}) {
    //#region Hooks
    const { t } = useLingui();
    const createMutation = useStandardMutation(createSnapshotMutation);
    const navigate = useNavigate();
    //#endregion

    const items = sortByCreatedAtDesc(getReplicasByType(vm, "snapshot"));
    const latestItem = first(items);

    const price = vm.priceCalc.prices.snapshotsPrice;

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Snapshots</Trans>} description={description_snapshots} />

            {items.length ? (
                <>
                    <DetailsTable>
                        <DetailsTableRow title={t`Est. monthly cost`}>
                            <PriceWithDiscount price={price} billingAccountId={vm.billing_account} allowFree />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Latest snapshot`}>
                            <LongDate date={latestItem!.created_at} />
                        </DetailsTableRow>
                    </DetailsTable>

                    <WTable>
                        <thead>
                            <tr>
                                <th>
                                    <Trans>UUID</Trans>
                                </th>
                                <th>
                                    <Trans>Size (GB)</Trans>
                                </th>
                                <th>
                                    <Trans>Created</Trans>
                                </th>
                                <th />
                            </tr>
                        </thead>
                        <WTableBody>
                            {items.map((item) => (
                                <tr key={item.uuid}>
                                    <td>
                                        <ClipBoardTooltip>{item.uuid}</ClipBoardTooltip>
                                    </td>
                                    <td>{item.size}</td>
                                    <td>
                                        <LongDate date={item.created_at} />
                                    </td>
                                    <td className="text-right">
                                        <div className="flex gap-0.5em justify-end">
                                            <WToolButton
                                                icon="jp-icon-add"
                                                label={t`Create new`}
                                                inTable
                                                action={async () =>
                                                    await navigate(
                                                        createVmFromReplicaLink(
                                                            getSourceVmForReplica(vms, vm, item),
                                                            item,
                                                            vpc,
                                                        ),
                                                    )
                                                }
                                            />
                                            <VmRestoreFromReplicaModal vm={vm} replica={item} inTable />
                                            <VmDeleteReplicaModal vm={vm} replica={item} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </WTableBody>
                    </WTable>
                </>
            ) : (
                <VmEstimatedReplicaPriceModal vm={vm} type="snapshot" billingAccountId={vm.billing_account} />
            )}

            <div>
                <WButton
                    color="primary"
                    size="bar"
                    variant="border"
                    icon="jp-camera-icon"
                    action={async () => {
                        await createMutation.mutateAsync({ location: vm.location, body: { uuid: vm.uuid } });
                    }}
                >
                    <Trans>Take snapshot</Trans>
                </WButton>
            </div>
        </ContentPane>
    );
}
